import { useEffect, useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
//CSS
import '../../styles/main.css'

// Components
import { Button, ButtonTheme, ButtonVariant, Heading, TypographyType, TypographyColor, Modal, Slider, InfoPanel, Body, BodyColor, BodyType } from '../../ui-elements';
import { useCIFService, useMapsService } from '../../services';
import { brandList, checkboxState, cifTypeState, errorState, filterDefaultState, filterLoadingState, filtersState, geoState, overlayCountState, poiMapColorState, recommendedRegionsState, selectedBrands, selectedTabState, selAggLayerState, selPOICategories, userCurrencyState } from '../../states';
import { addRangeMinMax, cleanFilterTree, extractAllFiltersWithSafeRange, getCountOfChangedRanges, getParentGeoId, hasAdjusted, indexToLetter, isAnyRangeChangedInFilters, removeAllRangeMinMax, resetRangesToMinMax, syncRangesFromFilter, updateRangeByKey, upsertFilterTree, useHandleError } from '../../helpers';
import { RouteConstants } from '../../constants';

interface FilterRegionModalProps {
    showFilterRegionModal: boolean;
    setShowFilterRegionModal: (showFilterRegionModal: boolean) => void;
    sliders: any[];
    setSliders: (sliders: any[]) => void;
}

export const FilterRegionModal = ({ showFilterRegionModal, setShowFilterRegionModal, sliders, setSliders }: FilterRegionModalProps) => {
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const cifServices = useCIFService();
    const mapService = useMapsService();
    const { getErrorMsg } = useHandleError();
    const setError = useSetRecoilState(errorState);
    const isChecked = useRecoilValue(checkboxState);
    const selectedGeo = useRecoilValue(geoState);
    const cifType = useRecoilValue(cifTypeState);
    const selCategoryList = useRecoilValue(selPOICategories);
    const selCurrency = useRecoilValue(userCurrencyState);
    const poiColors = useRecoilValue(poiMapColorState);
    const [overlays, setOverlays] = useState<any[]>([]);
    const [filters, setFilters] = useRecoilState<any[]>(filtersState);
    const [filtersdefaults, setFiltersdefault] = useRecoilState<any[]>(filterDefaultState);
    const [isFilterLoading, setIsFilterLoading] = useRecoilState<boolean>(filterLoadingState);
    const { adjacency } = useRecoilValue(checkboxState);
    const [overlayCount, setOverlayCount] = useRecoilState<any>(overlayCountState);
    const [isLoading, setIsLoading] = useState(false);
    const [isSliderMoving, setIsSliderMoving] = useState(false);
    const [districts, setDistricts] = useState<{ filtered_districts: number, all_districts: number }>({ filtered_districts: 0, all_districts: 0 });
    const [selFilter, setSelFilter] = useState<any>({});
    const [expandedChildIndices, setExpandedChildIndices] = useState<number[]>([]);
    const selectedTab = useRecoilValue(selectedTabState);
    const [recommendedRegionsPayload, setRecommendedRegionsPayload] = useRecoilState(recommendedRegionsState);
    const setBrands = useSetRecoilState(brandList);
    const setSelBrands = useSetRecoilState(selectedBrands);
    const selAggLayer = useRecoilValue(selAggLayerState);
    const catchment = searchParams.get('catchment');


    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return Number(geo_id);
    }, [selectedGeo]);

    const isDisabled =
        (pathname === RouteConstants.root && (!!selectedGeo.village || isChecked?.catchments || selAggLayer?.key === 'hexgrid')) ||
        (pathname === RouteConstants.catchments && catchment !== null && selAggLayer?.key !== 7);


    const isSliderChanged = isAnyRangeChangedInFilters(filters, poiColors?.rootProperties?.filters || []);
    const totalFilterCount = Object.values(overlayCount).reduce((acc: number, item) => acc + (item as number), 0);

    const getSliderOverlay = async () => {
        // setSpinnerLite(prev => prev + 1);
        setIsFilterLoading(true);
        try {
            const response = await cifServices.getOverlayFilters(searchParams?.get('catchment') ? Number(searchParams?.get('catchment')) : geoCode, (geoCode == selectedGeo.district && !selectedGeo.sub_district && !searchParams?.get('catchment')) ? selectedTab : null);
            if (response) setOverlays(response);
        } catch (error: any) {
            getErrorMsg(error);
        } finally {
            setIsFilterLoading(false);
            // setSpinnerLite(prev => prev - 1);
        }
    }

    const fetchFilteredDistricts = async () => {
        if (filters?.length === 0 || (!catchment && selectedGeo.village) || isDisabled) return;

        setIsLoading(true);
        try {
            const response = await cifServices.getFilteredDistricts({ filters, geoCode: searchParams?.get('catchment') ? Number(searchParams?.get('catchment')) : geoCode, selCurrency, overlays, sub_level: (geoCode == selectedGeo.district && !selectedGeo.sub_district) ? selectedTab : null });
            if (response) setDistricts(response);
        }
        catch (error: any) {
            getErrorMsg(error);
        } finally {
            setIsLoading(false);
            setIsSliderMoving(false);
        }
    }

    const getPayload = (geoId: number) => {
        geoId = searchParams?.get('catchment') ? Number(searchParams?.get('catchment')) : geoId;
        const selMetrics = JSON.parse(localStorage?.getItem("selMetrics") || '{}');

        const metric_type = (!selMetrics || Object.values(selMetrics).filter((item: any) => !!item).length === 0)
            ? cifType.map(item => ({ key: item, ismetric: false }))
            : Object.values(selMetrics)
                .map((metric: any) => {
                    const { title, ...rest } = metric || {};
                    return { ...rest };
                }) || [];

        return {
            geoId,
            metric_type,
            filters,
            currency: selCurrency,
            subcategory: selCategoryList.subcategory,
            hex: selectedGeo?.district ? selAggLayer?.key === 'hexgrid' : false

        }
    }

    const openFilterRegionModal = () => {
        localStorage.setItem('filter_new', 'true');

        const firstEnabled = overlays?.find((item: any) => !item.isdisable);

        setSelFilter(() => firstEnabled ? sliders.find(slider => slider.key === firstEnabled.key) : null);
        setShowFilterRegionModal(true);
    };

    const toggleChildExpand = (index: number) => {
        setExpandedChildIndices(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
    };

    // Updated handleSliderChange

    const handleSliderChange = (key: string, newRangeMin: number, newRangeMax: number) => {
        // Set slider moving state to show loader immediately
        setIsSliderMoving(true);

        // Helper to find an item deeply by key
        function findItemRecursive(items: any[]): any | undefined {
            for (const item of items) {
                if (item.key === key) return item;

                if (Array.isArray(item.child_overlay)) {
                    const found = findItemRecursive(item.child_overlay);
                    if (found) return found;
                }

                if (Array.isArray(item.sub_child_overlay)) {
                    const found = findItemRecursive(item.sub_child_overlay);
                    if (found) return found;
                }
            }
            return undefined;
        }

        // Helper to upsert range values in deeply nested structure
        function upsertRecursiveTree(items: any[]): any[] {
            return items.map(item => {
                const updatedItem = { ...item };

                if (item.key === key) {
                    updatedItem.rangeMin = newRangeMin;
                    updatedItem.rangeMax = newRangeMax;
                    return updatedItem;
                }

                if (Array.isArray(item.child_overlay)) {
                    updatedItem.child_overlay = upsertRecursiveTree(item.child_overlay);
                }

                if (Array.isArray(item.sub_child_overlay)) {
                    updatedItem.sub_child_overlay = upsertRecursiveTree(item.sub_child_overlay);
                }

                return updatedItem;
            });
        }

        // 1. Find the original item for comparison
        const originalItem = findItemRecursive(filters);

        // 2. Deep clone existing sliders and filters before mutation
        const clonedSliders = structuredClone(sliders);
        const clonedFilters = structuredClone(filters);

        // 3. Upsert the range values in both sliders and filters
        const updatedSliders = upsertRecursiveTree(clonedSliders);
        const updatedFilters = upsertRecursiveTree(clonedFilters);

        // 4. Determine if we should remove custom range (user has reset manually)
        const isUserResetToDefault =
            originalItem &&
            originalItem.min === newRangeMin &&
            originalItem.max === newRangeMax &&
            (originalItem.rangeMin !== undefined || originalItem.rangeMax !== undefined);

        // 5. Update selected filter
        // 5. Deep clone selFilter and upsert just like sliders/filters
        let updatedSelFilter = structuredClone(selFilter);
        updatedSelFilter = upsertRecursiveTree([updatedSelFilter])[0]; // returns array, so take first item

        if (isUserResetToDefault) {
            const originalSelItem = findItemRecursive([selFilter]);
            if (originalSelItem) {
                const resetSel = updateRangeByKey(updatedSelFilter, key, originalSelItem.min, originalSelItem.max);
                updatedSelFilter = { ...resetSel };
            }
        }


        // 6. Apply all updates
        setSelFilter(updatedSelFilter);
        setSliders(updatedSliders);
        setFilters(updatedFilters);
    };






    const handleSelFilterChange = (key: string, isdisable: boolean) => {
        if (isdisable) return;

        setSelFilter(sliders.find(item => item.key === key) || null);
    }

    const resetAllFilters = () => {
        // Reset all sliders to min/max
        const resetSliders = sliders.map((slider: any) => resetRangesToMinMax(structuredClone(slider)));
        // Reset all filters by removing rangeMin/rangeMax
        const resetFilters = filters.map((filter: any) => removeAllRangeMinMax(structuredClone(filter)));

        setSliders(resetSliders);
        setFilters(resetFilters);

        // Update selected filter if present
        if (selFilter && selFilter.key) {
            const updatedSelFilter = resetSliders.find((item: any) => item.key === selFilter.key);
            setSelFilter(updatedSelFilter);
        }

        localStorage.setItem('filters', JSON.stringify(resetFilters));
    };

    const resetFilter = () => {
        if (!selFilter) return;

        const deepSliderClone = structuredClone(selFilter);
        const updatedSelSlider = resetRangesToMinMax(deepSliderClone);
        const updatedSlider = upsertFilterTree(sliders, updatedSelSlider);

        const selectedFromFilter = filters.find(item => item.key === selFilter.key);
        const updatedFilters = upsertFilterTree(filters, removeAllRangeMinMax(selectedFromFilter));

        setSelFilter(updatedSelSlider);
        setSliders(updatedSlider);
        setFilters(updatedFilters);
        localStorage.setItem('filters', JSON.stringify(updatedFilters));

    };



    const handleApplyFilter = async () => {
        setIsLoading(true);

        const cleanedFilters = filters.map(cleanFilterTree);
        if (geoCode !== Number(selectedGeo.village) && !searchParams?.get('catchment')) {
            setRecommendedRegionsPayload((prev: any) => ({
                ...prev,
                ...getPayload(Number(geoCode)),
                filters: cleanedFilters,
            }));
        }
        try {
            const abortController = new AbortController();
            const res = await mapService.getMapPoiColor({
                ...getPayload(Number(geoCode)),
                filters: cleanedFilters,
            }, abortController.signal);

            localStorage.setItem('filters', JSON.stringify(cleanedFilters));
            setShowFilterRegionModal(false);
            setError({
                type: 'MapInfo',
                message: 'Regions have been filtered out based on your selected criteria.'
            });
        } catch (error: any) {
            if (error?.name !== 'AbortError' && error?.name !== 'CanceledError') {
                console.error('Error applying filter:', error);
            }
        } finally {
            setIsLoading(false);
        }
    };


    const handleCloseModal = () => {
        const prevFilter = JSON.parse(localStorage.getItem('filters')!);

        setFilters(prevFilter);
        setSliders(updateAllSliderRangeWrtFilters(sliders, prevFilter));
        setShowFilterRegionModal(false);
    }

    const updateAllSliderRangeWrtFilters = (newSliders: any[], filters: any[]) => {
        return newSliders.map((slider: any) => {
            const filter = filters.find(item => item.key === slider.key);
            return filter ? syncRangesFromFilter(slider, filter) : addRangeMinMax(slider);
        });
    }

    //get data layer list to filter
    useEffect(() => {
        // if (isDisabled) return;
        getSliderOverlay();
    }, [geoCode, selectedTab, adjacency, catchment, isChecked?.catchments, selAggLayer?.key]);

    useEffect(() => {
        if (overlays.length > 0 && selCurrency) {
            setIsFilterLoading(true);
            const fetchAllSliders = async () => {
                try {
                    const filterFromLs = JSON.parse(localStorage.getItem('filters')!) || [];
                    const rawResponse = await cifServices.getAllFilterSliders(overlays, searchParams?.get('catchment') ? Number(searchParams?.get('catchment')) : geoCode, selCurrency, (geoCode == selectedGeo.district && !selectedGeo.sub_district) ? selectedTab : null);
                    setFiltersdefault(rawResponse);
                    let safeFilters = structuredClone(rawResponse);
                    let safeSliders = structuredClone(rawResponse);

                    if (filterFromLs?.length > 0) {
                        const filtersWithSafeRange = extractAllFiltersWithSafeRange(safeFilters, filterFromLs);
                        if (hasAdjusted(filtersWithSafeRange) && !isChecked?.catchments) {
                            setError({ type: 'MapInfo', message: `Filters have been adjusted to match this region's data range` });
                        }

                        setFilters(filtersWithSafeRange);

                        localStorage.setItem('filters', JSON.stringify(filtersWithSafeRange));
                        setIsFilterLoading(false);
                    } else {
                        setFilters(safeFilters);
                        localStorage.setItem('filters', JSON.stringify(safeFilters));
                        setIsFilterLoading(false);

                    }

                    const slidersWithUpdatedRange = updateAllSliderRangeWrtFilters(safeSliders, filterFromLs);
                    setSliders(slidersWithUpdatedRange);

                } catch (error) {
                    console.error('Failed to fetch all filter sliders:', error);
                }
            };

            fetchAllSliders();
        }
    }, [overlays, selCurrency]);

    useEffect(() => {
        if (filters?.length === 0 || (!catchment && selectedGeo.village) || isDisabled) return;

        const handler = setTimeout(() => {
            fetchFilteredDistricts();
        }, 500);

        return () => clearTimeout(handler);
    }, [filters]);

    useEffect(() => {
        if (sliders?.length === 0 || overlays.length === 0) return;

        const allOverlayCounts = overlays.reduce((acc: any, overlay: any) => {
            const slider = sliders.find((f: any) => f.key === overlay.key);
            const count = slider ? getCountOfChangedRanges(slider) : 0;

            acc[overlay.key] = count;
            return acc;
        }, {});

        setOverlayCount(allOverlayCounts);
    }, [sliders, overlays]);

    return (
        <div className={`w-100 padding-top-2 col-2 filter-region position-relative d-flex flex-row justify-content-center align-items-center d-inline-block ${isDisabled || isFilterLoading || selAggLayer == null ? 'disabled' : ''}`}>
            <Button
                theme={ButtonTheme.secondary}
                variant={ButtonVariant.bordered}
                type='button'
                onClick={() => openFilterRegionModal()}
                classname='d-flex flex-row align-items-center gap-2 w-90 justify-content-center'
                disabled={isDisabled || isFilterLoading || selAggLayer == null}
            >
                {totalFilterCount > 0 ? "Filters" : "Apply Filters"}
                {!(totalFilterCount === 0 || (selectedGeo.village && !catchment)) && <span className='filter-count'>{totalFilterCount}</span>}
            </Button>
            <div className="position-absolute rounded bottom-tooltip padding-3" >
                <Body
                    type={BodyType.p3}
                    color={BodyColor.secondary}
                >
                    Filters are not applicable at this level
                </Body>
            </div>
            <Modal
                showModal={showFilterRegionModal}
                classname="width-65vw height-80vh p-0 m-0 min-h-auto"
            >
                <div className='modal-header height-10 d-flex flex-row justify-content-between align-items-center w-100 gap-5 p-0 padding-5'>
                    <div className="d-flex flex-column justify-content-start align-items-start gap-2">
                        <Heading
                            title='Filter Regions'
                            type={TypographyType.h3}
                            colour={TypographyColor.primary}
                            classname='tex t-start fs-20 ff-poppins-bold'
                        />
                        <Heading
                            title='Regions that fall outside the selected threshold range have been filtered out'
                            type={TypographyType.h6}
                            colour={TypographyColor.secondary}
                            classname='text-start'
                        />
                    </div>
                    <Button
                        theme={ButtonTheme.secondary}
                        variant={ButtonVariant.bordered}
                        type='button'
                        classname='close-button d-flex justify-content-center align-items-center p-1 h-auto m-0'
                    >
                        <span
                            className="material-symbols-outlined cursor-pointer color-black fs-20 p-0"
                            onClick={handleCloseModal}
                        >
                            close
                        </span>
                    </Button>
                </div>
                <div className="modal-body height-80 width-64vw d-flex flex-row p-0">
                    <div className="col-3 border-right padding-top-bottom-5">
                        <ul className='list-unstyled w-100 d-flex flex-column justify-content-start align-items-start padding-top-bottom-4 gap-3 padding-left-right-1 padding-top-bottom-5'>
                            <li>
                                <Heading
                                    title='Data Layers'
                                    type={TypographyType.h6}
                                    colour={TypographyColor.muted}
                                    classname='text-start padding-left-right-5'
                                />
                            </li>
                            {overlays?.length > 0 && overlays?.map((data) => (
                                <li
                                    key={data.key}
                                    onClick={() => handleSelFilterChange(data.key, data.isdisable)}
                                    className={`${data.isdisable === true ? '' : 'cursor-pointer'} d-flex flex-row align-items-center justify-content-start padding-left-right-5 padding-top-bottom-1 sel-overlay-type ${selFilter.key === data.key ? 'selected' : ''}`}
                                >
                                    <Heading
                                        title={`${data.overlay} ${!data.isdisable ? `(${String(overlayCount[data.key] ?? 0).padStart(2, '0')}/${data.count})` : ''}`}
                                        type={TypographyType.h5}
                                        colour={data.isdisable ? TypographyColor.muted : TypographyColor.secondary}
                                        classname={`text-start ${selFilter.key === data.key && 'ff-poppins-extra-bold'}`}
                                    />
                                    {data.isdisable && <InfoPanel classname='margin-left-1' text="This data layer does not support filters at this level because there is no data coverage at this level of granularity." />}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="col-9 d-flex flex-column justify-content-start align-items-start padding-top-bottom-4 overflow-auto gap-2">
                        <div className="d-flex flex-column justify-content-start align-items-start w-100 padding-top-bottom-5 gap-1 padding-left-right-30">
                            <div className="d-flex flex-row justify-content-between align-items-center w-100 padding-left-right-5">
                                <Heading
                                    title={selFilter.overlay}
                                    type={TypographyType.h5}
                                    colour={TypographyColor.secondary}
                                    classname='text-start ff-poppins-bold'
                                />
                                <Button
                                    theme={ButtonTheme.primary}
                                    variant={ButtonVariant.transparent}
                                    type='button'
                                    onClick={() => resetFilter()}
                                    classname='d-flex flex-row align-items-center text-underline p-0 h-auto'
                                >
                                    Reset
                                </Button>
                            </div>
                            <Heading
                                title={selFilter.sub_heading}
                                type={TypographyType.h6}
                                colour={TypographyColor.muted}
                                classname='text-start padding-left-right-5'
                            />
                            <div className="w-100 d-flex flex-row justify-content-between align-items-center">
                                <Slider
                                    min={selFilter.min}
                                    max={selFilter.max}
                                    rangeMax={selFilter.rangeMax}
                                    rangeMin={selFilter.rangeMin}
                                    sliderKey={selFilter.key}
                                    onRangeChange={(newMin, newMax) => handleSliderChange(selFilter.key, newMin, newMax)}
                                />
                            </div>
                        </div>
                        {selFilter.key === 'points_of_interest' &&
                            <div className='w-100 d-flex flex-column justify-content-start align-items-start gap-1 padding-top-5 border-top padding-left-right-30'>
                                <Heading
                                    title='Breakdown of PoIs'
                                    type={TypographyType.h5}
                                    colour={TypographyColor.secondary}
                                    classname='padding-left-right-5 t-start fs-15'
                                />
                                <Heading
                                    title='Filter across regions by the individual count of PoI categories and sub-categories'
                                    type={TypographyType.h6}
                                    colour={TypographyColor.muted}
                                    classname='padding-left-right-5 text-start'
                                />
                            </div>}
                        {selFilter?.child_overlay?.length > 0 &&
                            selFilter.child_overlay.map((child: any, childIndex: number) => {
                                const isExpanded = expandedChildIndices.includes(childIndex);
                                return (
                                    <div
                                        key={child.overlay}
                                        className={`w-100 d-flex flex-column justify-content-start align-items-start gap-1 padding-top-bottom-20 padding-left-right-30 ${(selFilter.key === 'points_of_interest' && childIndex === 0) ? '' : 'border-top'}`}
                                    >
                                        <div className="w-100 d-flex justify-content-between align-items-center">
                                            <div className='d-flex flex-row align-items-center'>
                                                <Heading
                                                    title={`${childIndex + 1}. ${child.overlay} `}
                                                    type={TypographyType.h6}
                                                    colour={TypographyColor.primary}
                                                    classname='text-start padding-left-right-2 padding-top-bottom-1'
                                                />
                                                <img src={child?.mbp_icon} />
                                            </div>
                                            {child?.sub_child_overlay?.length > 0 && (
                                                <div className="cursor-pointer material-symbols-outlined fs-18 cursor-pointer fw-bold color-black-5" onClick={() => toggleChildExpand(childIndex)}>
                                                    {isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                                                </div>
                                            )}
                                        </div>
                                        {selFilter.key !== 'points_of_interest' && (
                                            <Heading
                                                title={child.sub_heading}
                                                type={TypographyType.h6}
                                                colour={TypographyColor.muted}
                                                classname='text-start padding-left-right-5'
                                            />
                                        )}

                                        <div className="w-100 d-flex flex-row justify-content-between align-items-center">
                                            <Slider
                                                min={child.min}
                                                max={child.max}
                                                rangeMax={child.rangeMax}
                                                rangeMin={child.rangeMin}
                                                sliderKey={child.key}
                                                onRangeChange={(newMin, newMax) => handleSliderChange(child.key, newMin, newMax)}
                                            />
                                        </div>
                                        {child?.sub_child_overlay?.length > 0 && (
                                            <div className="padding-5 w-100" style={{ display: isExpanded ? 'block' : 'none' }}>
                                                {child.sub_child_overlay.map((subChild: any, subChildIndex: number) => (
                                                    <div
                                                        key={subChild.overlay}
                                                        className='w-100 d-flex flex-column justify-content-start align-items-start padding-5 gap-1'
                                                    >
                                                        <Heading
                                                            title={`${indexToLetter(subChildIndex)}. ${subChild.overlay}`}
                                                            type={TypographyType.h6}
                                                            colour={TypographyColor.primary}
                                                            classname='text-start'
                                                        />

                                                        {selFilter.key !== 'points_of_interest' && (
                                                            <Heading
                                                                title={subChild.sub_heading}
                                                                type={TypographyType.h6}
                                                                colour={TypographyColor.muted}
                                                                classname='text-start'
                                                            />
                                                        )}
                                                        <div className="w-100 d-flex flex-row justify-content-between align-items-center">
                                                            <Slider
                                                                min={subChild.min}
                                                                max={subChild.max}
                                                                rangeMax={subChild.rangeMax}
                                                                rangeMin={subChild.rangeMin}
                                                                sliderKey={subChild.key}
                                                                onRangeChange={(newMin, newMax) => handleSliderChange(subChild.key, newMin, newMax)}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}


                                    </div>
                                );
                            })}
                    </div>
                </div>
                <div className="modal-footer height-10 padding-left-right-5 gap-3">
                    <Button
                        theme={ButtonTheme.primary}
                        variant={ButtonVariant.transparent}
                        type='button'
                        onClick={resetAllFilters}
                        classname='d-flex flex-row align-items-center gap-1 text-underline m-0'
                    >
                        Reset all filters
                    </Button>
                    <Button
                        theme={ButtonTheme.primary}
                        variant={ButtonVariant.bordered}
                        type='button'
                        classname={`d-flex flex-row align-items-center justify-content-center m-0 width-13vw ${(isLoading || isSliderMoving) ? 'pe-none' : ''}`}
                        disabled={isSliderMoving || isLoading}
                        onClick={handleApplyFilter}
                    >
                        {(isLoading || isSliderMoving) ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : (
                            `View ${districts.filtered_districts}/${districts.all_districts} regions`
                        )}
                    </Button>
                </div>
            </Modal >
        </div >
    )
}

